import { useAuthStore, parseJwtClaims } from '@/stores/authStore'
import type {
  ApiResponse,
  TokenPairResponse,
  LoginRequest,
  RegisterRequest,
  Organization,
  UpdateOrgRequest,
  User,
  ApiKey,
  IssuedApiKey,
  LLMUsageSummary,
  LLMUsageRecord,
  TierBreakdown,
  LLMBudget,
  UpsertBudgetRequest,
  PriceEntry,
  MessageResponse,
} from '@/api/types'
import { isEmbedded } from '@/lib/embed'
import { requestTokenFromParent, markEmbedSessionLost } from '@/lib/embedBridge'

// VITE_BASE_URL (optional): absolute API origin for production deployments where
// no reverse proxy forwards /api/* to the backend (e.g. "https://api.example.com").
// Leave empty (default) to use relative /api/* paths proxied by Vite in dev or
// by nginx/caddy in production.
// VITE_API_URL is only used by vite.config.ts to set the dev proxy target.
const API_BASE = (import.meta.env.VITE_BASE_URL as string | undefined)?.replace(/\/$/, '') ?? ''

// ── Token refresh mutex ───────────────────────────────────────────────────────
// Prevents multiple concurrent 401 responses from each triggering their own
// refresh request. All callers share the same in-flight promise.

let refreshPromise: Promise<string> | null = null

async function getFreshToken(): Promise<string> {
  // Embedded: the partner parent owns refreshing — ask it, never redirect the frame to /login.
  if (isEmbedded()) {
    if (!refreshPromise) {
      refreshPromise = requestTokenFromParent()
        .catch((err) => {
          markEmbedSessionLost()
          throw err
        })
        .finally(() => {
          refreshPromise = null
        })
    }
    return refreshPromise
  }

  const store = useAuthStore.getState()
  if (!store.refreshToken) {
    store.clearSession()
    window.location.href = '/login'
    throw new Error('No refresh token available')
  }

  if (!refreshPromise) {
    refreshPromise = (async () => {
      const resp = await fetch(`${API_BASE}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: store.refreshToken }),
      })
      if (!resp.ok) {
        store.clearSession()
        window.location.href = '/login'
        throw new Error('Session expired — please log in again')
      }
      const data = (await resp.json()) as TokenPairResponse
      const claims = parseJwtClaims(data.access_token)
      store.updateTokens(data.access_token, data.refresh_token, data.expires_at)
      // Also update role in case it changed
      store.setSession({
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresAt: data.expires_at,
        userId: store.userId!,
        orgId: store.orgId!,
        role: claims.role,
      })
      return data.access_token
    })().finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

// ── Base HTTP client ──────────────────────────────────────────────────────────

async function request<T>(
  endpoint: string,
  options: RequestInit = {},
  authenticated = true
): Promise<T> {
  const store = useAuthStore.getState()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }

  if (authenticated && store.accessToken) {
    headers['Authorization'] = `Bearer ${store.accessToken}`
  }

  const url = `${API_BASE}${endpoint}`
  const response = await fetch(url, { ...options, headers })

  if (response.status === 401 && authenticated) {
    const freshToken = await getFreshToken()
    const retryHeaders = { ...headers, Authorization: `Bearer ${freshToken}` }
    const retry = await fetch(url, { ...options, headers: retryHeaders })

    if (!retry.ok) {
      const body = await retry.json().catch(() => ({}))
      throw new Error(
        `[${retry.status}] ${(body as { error?: { message?: string } })?.error?.message ?? retry.statusText}`
      )
    }
    if (retry.status === 204) return undefined as T
    return retry.json() as Promise<T>
  }

  if (!response.ok) {
    let message = response.statusText
    try {
      const body = await response.json()
      const err = (body as { error?: { code?: string; message?: string } })?.error
      // Keep the machine-readable code in the message: pages match on it to pick
      // user-facing copy (e.g. Login maps INVALID_CREDENTIALS), and the backend
      // sends it alongside the prose rather than inside it.
      message = [err?.code, err?.message ?? message].filter(Boolean).join(': ')
    } catch {
      // ignore parse errors
    }
    throw new Error(`[${response.status}] ${message}`)
  }

  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

function buildQuery(params?: Record<string, string | number | boolean | undefined>): string {
  if (!params) return ''
  const q = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&')
  return q ? `?${q}` : ''
}

// ── Auth API (public — no JWT) ────────────────────────────────────────────────

export const authApi = {
  login(body: LoginRequest): Promise<TokenPairResponse> {
    return request('/api/v1/auth/login', { method: 'POST', body: JSON.stringify(body) }, false)
  },

  register(body: RegisterRequest): Promise<TokenPairResponse> {
    return request('/api/v1/auth/register', { method: 'POST', body: JSON.stringify(body) }, false)
  },

  refresh(refreshToken: string): Promise<TokenPairResponse> {
    return request(
      '/api/v1/auth/refresh',
      { method: 'POST', body: JSON.stringify({ refresh_token: refreshToken }) },
      false
    )
  },

  logout(refreshToken: string): Promise<MessageResponse> {
    return request(
      '/api/v1/auth/logout',
      { method: 'POST', body: JSON.stringify({ refresh_token: refreshToken }) },
      false
    )
  },
}

// ── Organization API ──────────────────────────────────────────────────────────

export const orgApi = {
  get(orgId: string): Promise<ApiResponse<Organization>> {
    return request(`/api/v1/organizations/${orgId}`)
  },

  update(orgId: string, body: UpdateOrgRequest): Promise<ApiResponse<Organization>> {
    return request(`/api/v1/organizations/${orgId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    })
  },

  delete(orgId: string): Promise<MessageResponse> {
    return request(`/api/v1/organizations/${orgId}`, { method: 'DELETE' })
  },
}

// ── Users API ─────────────────────────────────────────────────────────────────

export const usersApi = {
  list(orgId: string): Promise<ApiResponse<User[]>> {
    return request(`/api/v1/organizations/${orgId}/users`)
  },

  get(orgId: string, userId: string): Promise<ApiResponse<User>> {
    return request(`/api/v1/organizations/${orgId}/users/${userId}`)
  },

  create(orgId: string, body: { email: string; password: string; role: string }): Promise<ApiResponse<User>> {
    return request(`/api/v1/organizations/${orgId}/users`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },

  updateRole(orgId: string, userId: string, role: string): Promise<MessageResponse> {
    return request(`/api/v1/organizations/${orgId}/users/${userId}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    })
  },

  changePassword(orgId: string, userId: string, body: { current_password?: string; new_password: string }): Promise<MessageResponse> {
    return request(`/api/v1/organizations/${orgId}/users/${userId}/password`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    })
  },

  remove(orgId: string, userId: string): Promise<MessageResponse> {
    return request(`/api/v1/organizations/${orgId}/users/${userId}`, { method: 'DELETE' })
  },
}

// ── API Keys API ──────────────────────────────────────────────────────────────

export const apiKeysApi = {
  list(orgId: string): Promise<ApiResponse<ApiKey[]>> {
    return request(`/api/v1/organizations/${orgId}/api-keys`)
  },

  // Returns directly (not wrapped in {success, data}). ownerId omitted → the caller owns it.
  issue(orgId: string, label: string, ownerId?: string): Promise<IssuedApiKey> {
    return request(`/api/v1/organizations/${orgId}/api-keys`, {
      method: 'POST',
      body: JSON.stringify(ownerId ? { label, owner_id: ownerId } : { label }),
    })
  },

  revoke(orgId: string, keyId: string): Promise<MessageResponse> {
    return request(`/api/v1/organizations/${orgId}/api-keys/${keyId}`, { method: 'DELETE' })
  },
}

// ── Billing API ───────────────────────────────────────────────────────────────

export const billingApi = {
  getUsage(orgId: string, period?: string): Promise<ApiResponse<LLMUsageSummary>> {
    return request(`/api/v1/organizations/${orgId}/llm/usage${buildQuery({ period })}`)
  },

  listRecords(
    orgId: string,
    period?: string,
    limit?: number
  ): Promise<ApiResponse<LLMUsageRecord[]>> {
    return request(
      `/api/v1/organizations/${orgId}/llm/usage/records${buildQuery({ period, limit })}`
    )
  },

  getTierBreakdown(orgId: string, period?: string): Promise<ApiResponse<TierBreakdown>> {
    return request(`/api/v1/organizations/${orgId}/llm/usage/tiers${buildQuery({ period })}`)
  },

  getPricing(orgId: string): Promise<ApiResponse<PriceEntry[]>> {
    return request(`/api/v1/organizations/${orgId}/llm/pricing`)
  },

  getBudget(orgId: string): Promise<ApiResponse<LLMBudget>> {
    return request(`/api/v1/organizations/${orgId}/llm/budget`)
  },

  // Returns {message} directly (not wrapped)
  upsertBudget(orgId: string, body: UpsertBudgetRequest): Promise<MessageResponse> {
    return request(`/api/v1/organizations/${orgId}/llm/budget`, {
      method: 'PUT',
      body: JSON.stringify(body),
    })
  },
}
